import { useCallback, useEffect, useState } from 'react';

const SIDEBAR_COLLAPSED_KEY = 'vivicta.nav.sidebarCollapsed';
const COLLAPSED_ATTRIBUTE = 'data-sidebar-collapsed';

const readCollapsed = (): boolean => {
  try {
    return globalThis.localStorage?.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

/**
 * Whether the desktop sidebar is hidden, remembered across visits. Mirrored
 * onto `<html>` as `data-sidebar-collapsed`, which portal.css turns into
 * `--vivicta-sidebar-width: 0` — the routed content's padding (theme.ts) and
 * the top bar's left edge both read that variable, so they widen with it.
 */
export const useSidebarCollapsed = () => {
  const [collapsed, setCollapsed] = useState(readCollapsed);

  useEffect(() => {
    document.documentElement.toggleAttribute(COLLAPSED_ATTRIBUTE, collapsed);
    try {
      globalThis.localStorage?.setItem(
        SIDEBAR_COLLAPSED_KEY,
        String(collapsed),
      );
    } catch {
      // Storage unavailable — the choice lasts until the page reloads.
    }
  }, [collapsed]);

  useEffect(
    () => () => document.documentElement.removeAttribute(COLLAPSED_ATTRIBUTE),
    [],
  );

  const toggle = useCallback(() => setCollapsed(value => !value), []);

  return { collapsed, toggle };
};
